import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { IBannerMatch } from '@/interface/IBannerMatch';
import { MarketPage, SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { getFilter } from '@/libraries/oddsApi/helpers';
import { parseISOString } from '@/libraries/utils/date';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { EnumOddsCategoryType, EnumTimeZoneType, useSportsZoneFlagsBannerEventsQuery as _useSportsZoneFlagsBannerEventsQuery } from '@/types';

type UseSportsZoneFlagsBannerEventsQueryProps = {
    sportType: MaybeRef<SportType>;
    tournamentIds?: MaybeRef<number[] | undefined>;
    tournamentNames?: MaybeRef<string[] | undefined>;
}

export function useSportsZoneFlagsBannerEventsQuery({
    sportType,
    tournamentIds,
    tournamentNames,
}: UseSportsZoneFlagsBannerEventsQueryProps) {
    const event = ref<IBannerMatch | null>(null);

    const variable = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            filter: getFilter(MarketPage.All),
            timeZone: EnumTimeZoneType.UTC_8,
            oddsCategory: EnumOddsCategoryType.All,
            tournamentIds: get(tournamentIds),
            tournamentNames: get(tournamentNames),
        },
    }));

    const { onResult, refetch, loaded } = _useSportsZoneFlagsBannerEventsQuery(
        variable,
        { pollInterval: 60000 },
    );

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const { filterEFootballLeagueIds } = storeToRefs(useFeatureToggleStore());

    onResult((result) => {
        const formattedEvents: IBannerMatch[] = result?.filter(event => BusinessLogicHelper.isEventAllowed(customerSettings.value, event, filterEFootballLeagueIds.value))
            .map(event => ({
                isLive: event.isLive,
                awayTeam: {
                    countryName: event.awayTeam?.countryName ?? '',
                },
                homeTeam: {
                    countryName: event.homeTeam?.countryName ?? '',
                },
                showTime: event.showTime,
                showTimeType: event.showTimeType,
                eventCode: event.eventCode,
                kickoffTime: parseISOString(event.absKickoffTime),
            })) ?? [];

        const sortedEvents = orderBy<IBannerMatch>(formattedEvents, [x => x.isLive, x => x.kickoffTime, x => x.eventCode], ['desc', 'asc', 'asc']);
        event.value = sortedEvents[0];
    });

    return {
        event,
        loaded,
        refetch,
    };
}
